import { useEffect, useRef, useState } from 'react'

import { TVisibilityTrigger } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'

import { VALIDATOR_DELAY } from './constants/validator'
import { ValidationContext } from './context/ValidationContext'
import { baseValidation } from './helpers/baseValidation'
import { checkDepsChanged } from './helpers/checkDepsChanged'
import { IValidationProcess, TChangeStatus } from './models/IBaseValidation'
import { IValidateResult, ValidationStatus } from './models/IValidationStatus'
import { IValidatorProps } from './models/IValidatorProps'

export const Validator = (props: IValidatorProps) => {
  const [validationText, setText] = useState<string>('')
  const firstValidationRef = useRef<boolean>(false)
  const prevValueRef = useRef<{ value?: string; deps?: unknown[] }>({})
  const currentIdRef = useRef<string>('')
  const isErrorMessageShowRef = useRef<boolean>(false)
  const [validationProcess] = useState<IValidationProcess>({
    callback: undefined,
    timeout: 0,
  })

  useEffect(() => {
    const { checkFn, value } = props

    const isValueChanged = prevValueRef.current.value !== value
    const isDepsChanged = checkDepsChanged(prevValueRef.current.deps, props.valueDependencies)

    if (!firstValidationRef.current || isValueChanged || isDepsChanged) {
      prevValueRef.current = {
        value: value,
        deps: props.valueDependencies,
      }
    } else {
      return
    }

    const changeStatus: TChangeStatus = (
      status: ValidationStatus,
      text: string,
      resolver: (d: IValidateResult) => void
    ) => {
      resolver({
        field: props.field || '',
        status,
        text,
      })

      if (props.checkFn && props.onChangeValidation) {
        props.onChangeValidation(status)
      }
    }

    const changeStatusHandler = (r: IValidateResult) => {
      const promise = Promise.resolve(r)
      validationProcess.callback?.process(promise)
    }

    const clearTimer = () => {
      clearTimeout(validationProcess.timeout)
      validationProcess.timeout = 0
    }

    if (checkFn !== null) {
      const validation = () => {
        firstValidationRef.current = true
        baseValidation({
          currentIdRef,
          checkFn,
          value,
          validationProcess,
          changeStatus,
          setText,
        })
      }

      if (!validationProcess.timeout) {
        changeStatus(ValidationStatus.WIP, '', changeStatusHandler)
      }

      clearTimer()
      if (props.throttle !== false) {
        validationProcess.timeout = window.setTimeout(validation, VALIDATOR_DELAY)
      } else {
        validation()
      }
    } else {
      clearTimer()
      changeStatus(ValidationStatus.Valid, '', changeStatusHandler)
      setText('')
    }
  }, [props.value, props.checkFn, props.valueDependencies])

  const unregisterValidator = () => {
    window.clearTimeout(validationProcess.timeout)
    if (validationProcess.callback) {
      firstValidationRef.current = false
      validationProcess.callback.unregister()
      validationProcess.callback = undefined
    }
  }

  useEffect(() => {
    return unregisterValidator
  }, [])

  useEffect(() => {
    if (!props.checkFn) {
      unregisterValidator()
    }
  }, [props.checkFn])

  return (
    <ValidationContext.Consumer data-testid={'validator-root'}>
      {(validContext) => {
        if (!validContext?.register) {
          if (!isErrorMessageShowRef.current) {
            isErrorMessageShowRef.current = true
            console.warn('Validator must be child of validation/Controller')
          }
        } else if (!validationProcess.callback && !!props.checkFn) {
          validationProcess.callback = validContext.register()
        }

        return (
          <Tooltip
            className={props.classes?.root}
            visibilityTrigger={TVisibilityTrigger.ClickHover}
            visibilityCallback={(newValue: boolean) => {
              if (props.tooltipVisible === false) {
                return false
              }
              return !validationText ? false : newValue
            }}
            tipContent={() => {
              return <div>{validationText}</div>
            }}
            __rootTestId={'validationRoot'}
          >
            {props.children}
          </Tooltip>
        )
      }}
    </ValidationContext.Consumer>
  )
}
